import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  templatesApi,
  type CreateDocumentTemplateRequest,
  type UpdateDocumentTemplateDraftRequest,
  type ListDocumentTemplatesParams,
  type RestoreDocumentTemplateRequest,
  type CloneDocumentTemplateRequest,
  type ListEncounterSchemasParams,
  type CreateEncounterSchemaRequest,
  type UpdateEncounterSchemaRequest,
  type RestoreEncounterSchemaRequest,
} from '@/shared/api/transcriptor/templatesApi';

import type { DocumentTemplateParseRequest } from '../config/types';

// === Document Templates ===

export const fetchDocumentTemplates = createAsyncThunk(
  'templates/fetchDocumentTemplates',
  async (params: ListDocumentTemplatesParams) => {
    const response = await templatesApi.listDocumentTemplates({
      ...params,
      statuses: params.statuses ?? ['active', 'draft'],
    });

    return response;
  },
);

export const fetchDeletedDocumentTemplates = createAsyncThunk(
  'templates/fetchDeletedDocumentTemplates',
  async (params: Omit<ListDocumentTemplatesParams, 'statuses'>) => {
    const response = await templatesApi.listDocumentTemplates({
      ...params,
      statuses: ['deleted'],
    });

    return response;
  },
);

export const fetchDocumentTemplateById = createAsyncThunk(
  'templates/fetchDocumentTemplateById',
  async (id: string) => {
    const template = await templatesApi.getDocumentTemplate(id);

    return template;
  },
);

export const createDocumentTemplateVersion = createAsyncThunk(
  'templates/createDocumentTemplateVersion',
  async (data: CreateDocumentTemplateRequest) => {
    const template = await templatesApi.createDocumentTemplate(data);

    return template;
  },
);

export const updateDocumentTemplateDraft = createAsyncThunk(
  'templates/updateDocumentTemplateDraft',
  async ({
    id,
    data,
  }: {
    id: string;
    data: UpdateDocumentTemplateDraftRequest;
  }) => {
    const template = await templatesApi.updateDocumentTemplateDraft(id, data);

    return template;
  },
);

export const deleteDocumentTemplateThunk = createAsyncThunk(
  'templates/deleteDocumentTemplate',
  async (id: string) => {
    await templatesApi.deleteDocumentTemplate(id);

    return id;
  },
);

export const restoreDocumentTemplateThunk = createAsyncThunk(
  'templates/restoreDocumentTemplate',
  async ({
    id,
    data,
  }: {
    id: string;
    data?: RestoreDocumentTemplateRequest;
  }) => {
    await templatesApi.restoreDocumentTemplate(id, data);

    return id;
  },
);

export const cloneDocumentTemplateThunk = createAsyncThunk(
  'templates/cloneDocumentTemplate',
  async ({ id, data }: { id: string; data: CloneDocumentTemplateRequest }) => {
    const result = await templatesApi.cloneDocumentTemplate(id, data);

    return result;
  },
);

export const parseDocumentTemplateThunk = createAsyncThunk(
  'templates/parseDocumentTemplate',
  async (data: DocumentTemplateParseRequest, { signal }) => {
    const result = await templatesApi.parseDocumentTemplate(data, signal);

    return result;
  },
);

// === Encounter Schemas ===

export const fetchEncounterSchemas = createAsyncThunk(
  'templates/fetchEncounterSchemas',
  async (params: ListEncounterSchemasParams) => {
    const response = await templatesApi.listEncounterSchemas(params);

    return response;
  },
);

export const createEncounterSchemaThunk = createAsyncThunk(
  'templates/createEncounterSchema',
  async (data: CreateEncounterSchemaRequest) => {
    const schema = await templatesApi.createEncounterSchema(data);

    return schema;
  },
);

export const updateEncounterSchemaThunk = createAsyncThunk(
  'templates/updateEncounterSchema',
  async ({ id, data }: { id: string; data: UpdateEncounterSchemaRequest }) => {
    const schema = await templatesApi.updateEncounterSchema(id, data);

    return schema;
  },
);

export const fetchEncounterSchemaById = createAsyncThunk(
  'templates/fetchEncounterSchemaById',
  async (id: string) => {
    const schema = await templatesApi.getEncounterSchema(id);

    return schema;
  },
);

export const deleteEncounterSchemaThunk = createAsyncThunk(
  'templates/deleteEncounterSchema',
  async (id: string) => {
    await templatesApi.deleteEncounterSchema(id);

    return id;
  },
);

export const restoreEncounterSchemaThunk = createAsyncThunk(
  'templates/restoreEncounterSchema',
  async ({
    id,
    data,
  }: {
    id: string;
    data?: RestoreEncounterSchemaRequest;
  }) => {
    await templatesApi.restoreEncounterSchema(id, data);

    return id;
  },
);

export const cloneEncounterSchemaThunk = createAsyncThunk(
  'templates/cloneEncounterSchema',
  async ({
    id,
    data,
  }: {
    id: string;
    data: { target_organization_id: string };
  }) => {
    const result = await templatesApi.cloneEncounterSchema(id, data);

    return result;
  },
);
